// @flow
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { useGlobalStyles } from 'GlobalStyles';
import { useShowAlert } from 'hooks';
import usePageTitle from 'hooks/usePageTitle';
import DraftsProvider from 'providers/DraftsProvider';
import queryString from 'query-string';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import { When } from 'UI/components/atoms/When';
import DraftsManager from 'UI/components/organisms/DraftsManager';
import ContentPageLayout from 'UI/components/templates/ContentPageLayout';
import { PageTitles } from 'UI/constants/defaults';
import { EntityType } from 'UI/constants/entityTypes';
import { UIStatus } from 'UI/constants/status';

import { ApplicantLoader } from './ApplicantLoader';
import { DRAFT_REQUIRED_FIELDS } from './NewCandidate.constants';
import { useStyles } from './NewCandidate.styled';
import NewCandidateContent from './NewCandidateContent';
import { useNewCandidateFromUrl } from './NewCandidateContent.hooks';
import { getExternalIdAndSource, transformCandidateDrafts } from './NewCandidateContent.utils';

export type NewCandidateProps = {
  history: any,
  location: any
};

const INITIAL_APPLICANT_STATE = {
  status: UIStatus.Default,
  results: null,
  error: null,
  responseStatusCode: null
};

const NewCandidate = (props: NewCandidateProps) => {
  const { location } = props;
  const globalClasses = useGlobalStyles();
  const classes = useStyles();
  const { showAlert } = useShowAlert();

  const { hiringId, contactId, applicantId, resumeId } = queryString.parse(location.search);
  const isFromProfile = !!hiringId || !!contactId;

  const [selectedDraft, setSelectedDraft] = useState(null);
  const contentKey = selectedDraft?.id || hiringId || contactId || applicantId || resumeId || 0;

  const [isFromDirectory, setIsFromDirectory] = useState(isFromProfile);

  const { source, externalId } = getExternalIdAndSource({ applicantId, resumeId });

  const {
    state: applicantState,
    applicantFormData,
    setState,
    externalSource,
    resetExternalSource
  } = useNewCandidateFromUrl({ type: source, id: externalId });

  const isApplicantDataLoading = applicantState.status === UIStatus.Loading;

  usePageTitle({ title: PageTitles.CandidateCreate });

  useEffect(() => {
    if (!selectedDraft?.formData) return;

    if (!selectedDraft?.formData?.email)
      showAlert({
        severity: 'error',
        ...strings.candidates.creation.candidateWithoutEmail
      });
  }, [selectedDraft, showAlert]);

  const handleOnSelectDraft = value => {
    setSelectedDraft(value);
    setIsFromDirectory(false);

    setState(INITIAL_APPLICANT_STATE);
  };

  const handleOnResetForm = fromDirectory => {
    setSelectedDraft(null);
    setIsFromDirectory(fromDirectory);
    setState(INITIAL_APPLICANT_STATE);
    resetExternalSource();
  };

  return (
    <ContentPageLayout
      text={
        isFromDirectory
          ? strings.candidates.creation.titleFromDirectory
          : strings.candidates.creation.title
      }
      titleLabelProps={{ backNavigation: true }}
    >
      <div className={globalClasses.itemCreationDataWrapper}>
        <DraftsProvider
          type={EntityType.Candidate}
          validationFields={DRAFT_REQUIRED_FIELDS}
          transformer={transformCandidateDrafts}
        >
          <>
            <When
              condition={
                !applicantFormData && !isFromProfile && applicantState.status !== UIStatus.Loading
              }
            >
              <Alert
                className={classes.resumeParserAlertContainer}
                severity="info"
                action={
                  <Button
                    className={globalClasses.noTransform}
                    color="inherit"
                    component={Link}
                    to={EntityRoutes.CandidateCreateFromResume}
                  >
                    {strings.candidates.creation.fromResumeAlert.action}
                  </Button>
                }
              >
                <AlertTitle>{strings.candidates.creation.fromResumeAlert.title}</AlertTitle>
                {strings.candidates.creation.fromResumeAlert.description}
              </Alert>
            </When>

            <When condition={applicantState.status !== UIStatus.Default}>
              <ApplicantLoader state={applicantState} />
            </When>
            <When condition={!isFromProfile && !isApplicantDataLoading}>
              <DraftsManager type={EntityType.Candidate} onSelectDraft={handleOnSelectDraft} />
            </When>
            <When condition={!isApplicantDataLoading}>
              <NewCandidateContent
                key={contentKey}
                contactId={contactId || applicantFormData?.contactId}
                hiringId={hiringId || applicantFormData?.hiringId}
                initialApplicantData={applicantFormData}
                initiatingFromDirectory={isFromDirectory}
                isFromProfile={isFromProfile}
                onResetForm={handleOnResetForm}
                externalSource={externalSource}
                {...props}
              />
            </When>
          </>
        </DraftsProvider>
      </div>
    </ContentPageLayout>
  );
};

export default NewCandidate;
